import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryClient } from '@/lib/queryClient'
import { apiService } from '@/services/apiService'
import type { components } from '../../../../types/flex-api'

type Pool = components['schemas']['Pool']

type CreatePoolPayload = {
  accountID: string
  name: string
  isDefault?: boolean
  controlConfig?: {
    purpose: 'imbalance' | 'tou_stacking_poc' | 'intraday' | 'intraday_cloud'
    dryRun?: boolean
    multiTargetAsset?: boolean
    fallbackBehavior?: 'none' | 'last_possible_moment' | 'time_of_use'
  }
  forecastConfigs?: Array<{ purpose: 'gcp_supply' | 'gcp_feed_in' | 'gcp_without_ev' | 'ev'; isActive?: boolean }>
  tags?: Record<string, string>
}

type UpdatePoolPayload = {
  name?: string
  isDefault?: boolean
  tags?: Record<string, string | null>
  evChargingEfficiencyFactor?: number | null
  controlConfig?: {
    dryRun?: boolean
    fallbackBehavior?: 'none' | 'last_possible_moment' | 'time_of_use'
  }
  forecastConfigs?: Array<{
    purpose: 'gcp_supply' | 'gcp_feed_in' | 'gcp_without_ev' | 'ev'
    isActive?: boolean
  }> | null
}

async function fetchAllPools(): Promise<Pool[]> {
  const res = await apiService.pools.getAll()
  if (!res.success) throw new Error(res.error ?? 'Failed to fetch pools')
  return res.data!
}

export function usePools() {
  return useQuery({ queryKey: ['pools'], queryFn: fetchAllPools })
}

export function usePool(poolID: string) {
  return useQuery({
    queryKey: ['pools', poolID],
    queryFn: async () => {
      const res = await apiService.pools.getById(poolID)
      if (!res.success) throw new Error(res.error ?? 'Failed to fetch pool')
      return res.data!
    },
    enabled: !!poolID,
    initialData: () => {
      const pools = queryClient.getQueryData<Pool[]>(['pools'])
      return pools?.find((p) => p.poolID === poolID)
    },
    initialDataUpdatedAt: () => queryClient.getQueryState(['pools'])?.dataUpdatedAt,
  })
}

export function useCreatePool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (data: CreatePoolPayload) => {
      const res = await apiService.pools.create(data)
      if (!res.success) throw new Error(res.error ?? 'Failed to create pool')
      return res.data!
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pools'] }),
  })
}

export function useUpdatePool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ poolID, data }: { poolID: string; data: UpdatePoolPayload }) => {
      const res = await apiService.pools.update(poolID, data)
      if (!res.success) throw new Error(res.error ?? 'Failed to update pool')
      return res.data!
    },
    onSuccess: (_, { poolID }) => {
      qc.invalidateQueries({ queryKey: ['pools'] })
      qc.invalidateQueries({ queryKey: ['pools', poolID] })
    },
  })
}

export function useDeletePool() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async (poolID: string) => {
      const res = await apiService.pools.delete(poolID)
      if (!res.success) throw new Error(res.error ?? 'Failed to delete pool')
    },
    onSuccess: (_, poolID) => {
      qc.setQueryData<Pool[]>(['pools'], (old) => old?.filter((p) => p.poolID !== poolID) ?? [])
      qc.invalidateQueries({ queryKey: ['pools'] })
      qc.removeQueries({ queryKey: ['pools', poolID] })
    },
  })
}
